import React, { useCallback, useState } from 'react'
import { MerchandiseForm } from './MerchandiseForm'
import { useTranslation } from '@hooks/useTranslation'
import { Message } from 'semantic-ui-react'
import ToggleButton from '@commonComponents/formComponents/toggleButton'
import { CustomLoader } from '@commonComponents/formComponents/loader'
import ErrorBoundary from '@commonComponents/ErrorBoundary'
import MerchContainer from '../merchandiseDocuments/MerchContainer'
import MerchandiseDuty from '../dutyAndTaxInformation/MerchandiseDuty'
import TaxInformation from '../dutyAndTaxInformation/TaxInformation'
import AdditionalDocDetail from '../merchandiseDocuments/AdditionalDocument'
import PreviousDocDetail from '../merchandiseDocuments/PreviousDocument'
import SupportingDocDetail from '../merchandiseDocuments/SupportingDocument'
import RepeatableItemsContainer from '@containers/repeatableItemsContainer'
import { useMultipleMerchandiseItem } from '@EUForms/hooks/useMultipleMerchandiseItem'
import { useEvent } from '@hooks/useEvent'
import { useDispatch, useSelector } from 'react-redux'
import { itemActions } from '../../../reducer/ItemSlice'
import MerchandiseSummary from './MerchandiseSummary'
import { selectAdditionalDocList, selectCountryInfoDS, selectPreviousProcedureList, selectProcedure, selectpreferencesList } from "@EUForms/selectors";
import MerchandiseDetail from './MerchandiseDetail'
import { selectCountryList } from '@EUForms/selectors'
import PackagingDocument from '@EUForms/invoice/PackagingDocument'
import { useCurrentShipmentType } from '@hooks/useCurrentShipmentType'
import LineItemCharges from '../dutyAndTaxInformation/LineItemCharges'
import { useContext } from 'react'
import { InvoiceToggleContext } from '@commonComponents/accordion/AccordianInvoice'
import { procedureValidationMapping } from '../../../util'
import { addExtraSpace } from '@EUForms/utils'
import MerchandiseParties from './MerchandiseParties'
import MarkedDeleted from '../markedDeleted'
import MerchandiseSummaryEditable from './MerchandiseSummaryEditable'
import DutyAndTaxInformation from '../dutyAndTaxInformation'

const MerchandiseContainer = (props) => {
  const { invoiceId, merchandiseId, invoiceIndex, itemIndex, isInvoiceDisabled } = props
  const { state } = useContext(InvoiceToggleContext)
  const active = state[merchandiseId]

  const translate = useTranslation()
  const procedure = useSelector(selectProcedure)
  const { originCountry, destinationCountry } = useSelector(selectCountryInfoDS)
  // const docTypeList = useSelector(selectAdditionalDocList)
  let proceduresList = useSelector(selectPreviousProcedureList)
  let preferencesList = useSelector(selectpreferencesList)
  const procedureMapping = procedureValidationMapping[procedure] || []
  if (procedureMapping.length > 0) {
    proceduresList = proceduresList?.filter((item) => {
      return procedureMapping.some(proc => item?.value?.startsWith(proc))
    })
  }

  const { isImportShipment, isRFI, isRFIDisabled } = useCurrentShipmentType()
  const { isLoading, saveForm, deleteForm } = useEvent('invoiceTab', false)
  const dispatch = useDispatch()
  const countryList = useSelector(selectCountryList)

  const {
    updateMerchandiseItem,
    deleteMerchandiseItem,
    updateInvoiceHeader,
    updateInvoiceUpperHeader,
    updateMerchandiseHierarchy
  } = itemActions
  const { ...rest } = useMultipleMerchandiseItem({ invoiceId, merchandiseId })
  const dataSource = useSelector(
    (state) => state.invoiceReducer.invoice.merchandiseMap[merchandiseId]
  )
  const isMerchandiseDisabled = (isInvoiceDisabled || (isRFI && dataSource?.markDelete == '1') || null)
  const {
    supportingDocumentsList = [],
    additionalDocumentsList = [],
    taricAdditionalCodesList = [],
    nationalAdditionalCodesList = [],
  } = dataSource

  const saveData = useCallback(
    async ({ data }) => {
      const response = await saveForm(
        { merchandiseMap: { [merchandiseId]: { ...addExtraSpace(data), invoiceId } } },
        false
      )
      if (response) {
        const invoiceUpperHeader = response?.invoiceTab?.invoiceUpperHeader
        invoiceUpperHeader && dispatch(updateInvoiceUpperHeader(invoiceUpperHeader))
        const invoiceMap = response?.invoiceTab?.invoiceMap
        const invoiceHeader = (invoiceMap && invoiceMap[invoiceId]?.invoiceHeader) || ''
        invoiceHeader && dispatch(updateInvoiceHeader({ invoiceId, data: invoiceHeader }))
        const merchandiseMap = response?.invoiceTab?.merchandiseMap
        const merchData = merchandiseMap && merchandiseMap[merchandiseId]
        dispatch(updateMerchandiseItem({ merchandiseId, data: merchData }))
      }
    },
    [saveForm, invoiceId]
  )
  const deleteData = useCallback(async () => {
    const response = await deleteForm({ merchandiseIdList: [merchandiseId] })
    if (response) {
      const invoiceUpperHeader = response?.invoiceTab?.invoiceUpperHeader
      invoiceUpperHeader && dispatch(updateInvoiceUpperHeader(invoiceUpperHeader))
      const invoiceHeader = response?.invoiceTab?.invoiceMap[invoiceId]?.invoiceHeader
      invoiceHeader && dispatch(updateInvoiceHeader({ invoiceId, data: invoiceHeader }))
      dispatch(deleteMerchandiseItem({ merchandiseId, invoiceId }))
    }
  }, [invoiceId, deleteMerchandiseItem, updateInvoiceUpperHeader, deleteForm])

  const submit = useCallback(async (payload) => {
    const { doc, additionalCode1, additionalCode2, unitType, supUnitType } = payload

    let documentList = doc?.filter((docType) => {
      return docType && !(
        additionalDocumentsList?.some((item) => item.type === docType)
        || supportingDocumentsList?.some((item) => item.type === docType)
      )
    }) || []

    documentList = documentList?.map(docType => {
      let extraInfo = {}
      if ((docType?.startsWith('N') && docType?.length === 5) || (!isNaN(docType) && docType?.length === 5)) {
        extraInfo = { description: 'INF', saveTo: 'additionalDocumentsList' }
      } else if (docType?.startsWith('Y') || [1500, 1600, 1800, 1900, 1999].includes(Number(docType))) {
        extraInfo = { description: 'REF', saveTo: 'additionalDocumentsList' }
      } else if (
        ['A', 'C', 'D', 'E', 'I', 'L', 'N', 'U', 'X'].some(startCode => docType?.startsWith(startCode))
        || (![1500, 1600, 1800, 1900, 1999].includes(Number(docType)) && !isNaN(docType) && docType?.length === 4)
      ) {
        extraInfo = { saveTo: 'supportingDocumentsList' }
      }
      return { type: docType, ...extraInfo }
    })

    documentList.forEach(({ saveTo, ...restDocInfo }) => saveTo && rest.addItem(saveTo, { ...restDocInfo }))

    let _additionalCode1 = additionalCode1 && !taricAdditionalCodesList.some((item) => item.code === additionalCode1)
    let _additionalCode2 = additionalCode2 && !nationalAdditionalCodesList.some((item) => item.code === additionalCode2)

    _additionalCode1 && rest.addItem('taricAdditionalCodesList', { code: additionalCode1 })
    _additionalCode2 && rest.addItem('nationalAdditionalCodesList', { code: additionalCode2 })

    if (supUnitType) {
      let data = { unitOfMeasurement: supUnitType }
      let response = await saveForm(
        { merchandiseMap: { [merchandiseId]: { ...addExtraSpace(data), invoiceId } } },
        false
      )
      response && dispatch(updateMerchandiseItem({ merchandiseId, data }))
    }

  }, [additionalDocumentsList, supportingDocumentsList, taricAdditionalCodesList, nationalAdditionalCodesList])

  const onMarkMerchandiseDelete = useCallback((e, data) => {
    if (data.checked) {
      // dispatch(updateMerchandiseItem({ merchandiseId, data: { markDelete: '1' } }))
      dispatch(updateMerchandiseHierarchy({ merchandiseId, data: { markDelete: '1' } }))
      saveData({ merchandiseId, data: { markDelete: '1' } })
    } else {
      // dispatch(updateMerchandiseItem({ merchandiseId, data: { markDelete: '0' } }))
      dispatch(updateMerchandiseHierarchy({ merchandiseId, data: { markDelete: '0' } }))
      saveData({ merchandiseId, data: { markDelete: '0' } })
    }
  }, [merchandiseId])

  const onMarkMerchandiseDocDelete = useCallback((data, { id, tableName }) => {
    if (data.checked) {
      rest?.updateItem(id, tableName, { id, markDelete: '1' })
      rest.saveItem(tableName, { id, markDelete: '1' })
    } else {
      rest?.updateItem(id, tableName, { id, markDelete: '0' })
      rest.saveItem(tableName, { id, markDelete: '0' })
    }
  }, [])

  return (
    <ErrorBoundary>
      <div className={'accordian-section tab-contentInvoice '}>
        <Message style={{ padding: '10px' }}>
          {isLoading && <CustomLoader />}
          <ToggleButton
            id={`toggleMerchandise-${'invoiceId'}-${'merchandiseId'}`}
            title={(isActive) =>
              isActive ? (
                'Merchandise'
              ) : (
                // isImportShipment ?
                // <MerchandiseSummary
                //   id={merchandiseId}
                //   dataSource={dataSource}
                //   translate={translate}
                // /> :
                <MerchandiseDetail
                  procedure={procedure}
                  invoiceId={invoiceId}
                  merchandiseId={merchandiseId}
                  saveData={saveData}
                  updateData={updateMerchandiseItem}
                  renderForm={(params) => (
                    <MerchandiseSummaryEditable
                      {...params}
                      invoiceId={invoiceId}
                      merchandiseId={merchandiseId}
                      originCountry={originCountry}
                      destinationCountry={destinationCountry}
                      countryList={countryList}
                      proceduresList={proceduresList}
                      preferencesList={preferencesList}
                      submit={submit}
                      isMerchandiseDisabled={isMerchandiseDisabled}
                      isRFI={isRFI}
                      markedDeleted={isRFI ? () => <MarkedDeleted
                        label='Marked Deleted'
                        className='marked-deleted'
                        style={{ marginTop: '-4px' }}
                        disabled={(isRFIDisabled || isInvoiceDisabled)}
                        checked={(isInvoiceDisabled || dataSource?.markDelete == '1')}
                        onChange={onMarkMerchandiseDelete}
                      /> : null}
                    />
                  )}
                />
              )
            }
            toggleId={merchandiseId}
            // onClick={() => setActiveAccordianIndex(!activeAccordianIndex)}
            confirmationMsg='Merchandise Line Item'
            lineNumber={itemIndex + 1}
            onDelete={() => deleteData({ invoiceId })}
            // isActive={activeAccordianIndex}
            wrapButtonWhenInActive={true}
            containerStyle={{ height: '50px' }}
            markedDeleted={isRFI ? () => <MarkedDeleted
              label='Marked Deleted'
              className='marked-deleted'
              style={{ marginTop: '-4px' }}
              disabled={(isRFIDisabled || isInvoiceDisabled)}
              checked={(isInvoiceDisabled || dataSource?.markDelete == '1')}
              onChange={onMarkMerchandiseDelete}
            /> : null}
            sequenceNumber={dataSource?.sequenceNumber}
          />
          {active && (
            <>
              <MerchandiseDetail
                saveData={saveData}
                merchandiseId={merchandiseId}
                invoiceId={invoiceId}
                updateData={updateMerchandiseItem}
                procedure={procedure}
                renderForm={(params) => (
                  <MerchandiseForm
                    originCountry={originCountry}
                    destinationCountry={destinationCountry}
                    submit={submit}
                    packages={(packagesList) => (
                      <PackagingDocument
                        merchandiseId={merchandiseId}
                        packagesList={packagesList}
                        isMerchandiseDisabled={isMerchandiseDisabled}
                        isRFIDisabled={isRFIDisabled}
                        onMarkMerchandiseDocDelete={onMarkMerchandiseDocDelete}
                        {...rest}
                      />
                    )}
                    countryList={countryList}
                    proceduresList={proceduresList}
                    preferencesList={preferencesList}
                    isRFI={isRFI}
                    isMerchandiseDisabled={isMerchandiseDisabled}
                    markedDeleted={isRFI ? (params) => <MarkedDeleted
                      label='_'
                      className='marked-deleted marked-deleted-label-hidden'
                      disabled={isMerchandiseDisabled}
                      checked={(isMerchandiseDisabled || params?.markDelete)}
                      style={{ paddingTop: '4px' }}
                      onChange={
                        (e, data) => onMarkMerchandiseDocDelete(data, params)
                      }
                    /> : null}
                    {...params}
                  />
                )}
              />
              {/* {isImportShipment && (
                <>
                  <TaxInformation merchandiseId={merchandiseId} invoiceId={invoiceId} />
                  <MerchandiseDuty merchandiseId={merchandiseId} translate={translate} {...rest} />
                </>
              )} */}
              <MerchandiseParties
                translate={translate}
                invoiceId={invoiceId}
                merchandiseId={merchandiseId}
                invoiceIndex={invoiceIndex}
                itemIndex={itemIndex}
                isMerchandiseDisabled={isMerchandiseDisabled}
                isRFIDisabled={isRFIDisabled}
              />
              <RepeatableItemsContainer
                {...rest}
                tableName='previousDocumentsList'
                merchandiseId={merchandiseId}
                reducerName='invoiceReducer'
                title='PREVIOUS DOCUMENT'
                translate={translate}
                boxNumber='2/1'
                renderForm={(params) => <PreviousDocDetail
                  {...params}
                  isMerchandiseDisabled={isMerchandiseDisabled}
                  onMarkMerchandiseDocDelete={onMarkMerchandiseDocDelete}
                />}
                isAddDisabled={isMerchandiseDisabled}
              />
              <RepeatableItemsContainer
                {...rest}
                tableName='additionalDocumentsList'
                merchandiseId={merchandiseId}
                reducerName='invoiceReducer'
                title='ADDITIONAL DOCUMENT'
                translate={translate}
                boxNumber='2/2'
                renderForm={(params) => <AdditionalDocDetail
                  {...params}
                  isMerchandiseDisabled={isMerchandiseDisabled}
                  onMarkMerchandiseDocDelete={onMarkMerchandiseDocDelete}
                />}
                isAddDisabled={isMerchandiseDisabled}
              />
              <RepeatableItemsContainer
                {...rest}
                tableName='supportingDocumentsList'
                merchandiseId={merchandiseId}
                reducerName='invoiceReducer'
                title='SUPPORTING DOCUMENTS'
                translate={translate}
                boxNumber='2/3'
                renderForm={(params) => <SupportingDocDetail
                  {...params}
                  isMerchandiseDisabled={isMerchandiseDisabled}
                  onMarkMerchandiseDocDelete={onMarkMerchandiseDocDelete}
                />}
                isAddDisabled={isMerchandiseDisabled}
              />
              {procedure != 'H6' && <MerchContainer
                merchandiseId={merchandiseId}
                isMerchandiseDisabled={isMerchandiseDisabled}
                {...rest}
              />}

              {isImportShipment && (
                <DutyAndTaxInformation
                  {...{
                    invoiceId,
                    merchandiseId,
                    translate,
                    ...rest
                  }}
                />
              )}

              {!isImportShipment && (<LineItemCharges invoiceId={invoiceId} merchandiseId={merchandiseId} />)}
            </>
          )}
        </Message>
      </div>
    </ErrorBoundary>
  )
}

export default React.memo(MerchandiseContainer)
